import { useEffect, useState } from 'react';

import { InfiniteScroll, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import FarmsGridHeader from 'components/FarmsGrid/components/FarmsGridHeader';
import WithArrow from 'components/WithArrow';
import WithResize from 'components/WithResize';
import GridText from 'components/text/GridText';

import useGridTablesResizeObserver from 'hooks/useGridTablesResizeObserver';

import useTranslation from './hooks/useTranslation';

import FarmsGridRow from './components/FarmsGridRow';
import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const FarmsGrid: FunctionComponent<Props> = ({
    className = '',
    onNext,
    rows = [],
    testId = TEST_ID
}) => {
    const { LAST_ACTIVITY, LAST_EDITED, LOADING, NAME, NO_FARMS, NO_MORE_FARMS } = useTranslation();

    const { containerRef, handleResize } = useGridTablesResizeObserver('--farms-grid-row-columns');
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (!initialized) {
            const hasNext = typeof onNext === 'function';

            setHasNextPage(hasNext);

            if (hasNext) {
                setInitialized(true);
            }
        }
    }, [initialized, onNext]);

    const fetchNext = async () => {
        if (!hasNextPage || !onNext) {
            return;
        }

        const { done } = await onNext();

        setHasNextPage(!done);
    };

    const endingMessage = initialized && rows.length === 0 ? NO_FARMS : NO_MORE_FARMS;

    return (
        <VerticalContainer
            className={`${className} ${styles.farmsGrid}`}
            ref={containerRef}
            testId={testId}>
            <FarmsGridHeader>
                <WithResize onResize={handleResize}>
                    <WithArrow className={styles.withArrow}>
                        <GridText text={NAME} />
                    </WithArrow>
                </WithResize>

                <WithResize onResize={handleResize}>
                    <GridText text={LAST_EDITED} />
                </WithResize>

                <GridText text={LAST_ACTIVITY} />
            </FarmsGridHeader>

            <InfiniteScroll
                endingMessage={
                    <Wrapper className={styles.wrapper}>
                        <GridText text={endingMessage} />
                    </Wrapper>
                }
                loader={
                    <Wrapper className={styles.wrapper}>
                        <GridText text={LOADING} />
                    </Wrapper>
                }
                className={styles.infiniteScroll}
                fetchNextPage={fetchNext}
                hasNextPage={hasNextPage}>
                {rows.map(({ id, name, updatedAt }) => (
                    <FarmsGridRow id={id} key={id} lastEdited={updatedAt} name={name} />
                ))}
            </InfiniteScroll>
        </VerticalContainer>
    );
};

export default FarmsGrid;
